/**
 * Brick Lane unified analytics client.
 *
 * - Fetches non-secret marketing IDs from `/api/content/marketing-config` once.
 * - Lazily loads each tracker script only if its ID is configured.
 * - Provides a single `track(event, params)` API that fans out to every
 *   configured provider (Meta Pixel, GA4, Google Ads, TikTok Pixel, PostHog).
 * - All providers gracefully no-op when an ID is missing.
 */
import { contentAPI } from './api';

const API_URL = '';

// ---- Anonymous visitor identity ----
// `visitor_id`: persistent UUID per browser (localStorage) — counts unique visitors.
// `session_id`: ephemeral UUID per tab session (sessionStorage) — counts sessions.
// Privacy: no PII, no fingerprinting, no cross-site tracking. Cleared when the
// user clears site data, matching GDPR's spirit for anonymous analytics.
const _uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for old browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};
const _getVisitorId = () => {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem('bl_visitor_id');
    if (!id) { id = _uuid(); window.localStorage.setItem('bl_visitor_id', id); }
    return id;
  } catch { return ''; }
};
const _getSessionId = () => {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.sessionStorage.getItem('bl_session_id');
    if (!id) { id = _uuid(); window.sessionStorage.setItem('bl_session_id', id); }
    return id;
  } catch { return ''; }
};

let CONFIG = null;
let CONFIG_PROMISE = null;
const LOADED = { meta: false, ga4: false, gads: false, tiktok: false, posthog: false };

const isBrowser = typeof window !== 'undefined';

const loadScript = (src, id) =>
  new Promise((resolve) => {
    if (!isBrowser || document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = src;
    s.onload = resolve;
    s.onerror = resolve; // never block other providers
    document.head.appendChild(s);
  });

// ---- Meta Pixel ----
const initMetaPixel = (pixelId) => {
  if (LOADED.meta || !pixelId || !isBrowser) return;
  // Standard Facebook Pixel boot snippet
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  LOADED.meta = true;
};

// ---- Google Analytics 4 ----
const initGA4 = (measurementId) => {
  if (LOADED.ga4 || !measurementId || !isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: true });
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, 'gtag-script');
  LOADED.ga4 = true;
};

// ---- Google Ads (uses gtag) ----
const initGoogleAds = (adsId) => {
  if (LOADED.gads || !adsId || !isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('config', adsId);
  if (!document.getElementById('gtag-script')) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${adsId}`, 'gtag-script');
  }
  LOADED.gads = true;
};

// ---- TikTok Pixel ----
const initTikTok = (pixelId) => {
  if (LOADED.tiktok || !pixelId || !isBrowser) return;
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
    ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      var r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
      ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
      ttq._o = ttq._o || {}; ttq._o[e] = n || {};
      var o = document.createElement('script');
      o.type = 'text/javascript'; o.async = !0; o.src = r + '?sdkid=' + e + '&lib=' + t;
      var a = document.getElementsByTagName('script')[0]; a.parentNode.insertBefore(o, a);
    };
    ttq.load(pixelId); ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable */
  LOADED.tiktok = true;
};

// ---- PostHog (re-configures the snippet already in index.html with the new key) ----
const initPostHog = (key, host) => {
  if (LOADED.posthog || !key || !isBrowser || !window.posthog) return;
  try {
    // Re-init (safe — posthog snippet supports re-init).
    window.posthog.init(key, { api_host: host || 'https://us.i.posthog.com', person_profiles: 'identified_only' });
    LOADED.posthog = true;
  } catch (e) {
    // ignore
  }
};

/** Lazy-load & init all providers. Idempotent. */
export const initAnalytics = async () => {
  if (CONFIG) return CONFIG;
  if (!CONFIG_PROMISE) {
    CONFIG_PROMISE = contentAPI.getMarketingConfig().catch(() => ({}));
  }
  const cfg = (await CONFIG_PROMISE) || {};
  CONFIG = cfg;
  if (cfg.enabled === false) return cfg;
  initMetaPixel(cfg.meta_pixel_id);
  initGA4(cfg.ga4_measurement_id);
  initGoogleAds(cfg.google_ads_id);
  initTikTok(cfg.tiktok_pixel_id);
  initPostHog(cfg.posthog_key, cfg.posthog_host);
  return cfg;
};

/** Fire an event across every configured provider. */
export const track = (eventName, params = {}) => {
  if (!isBrowser) return;
  const cfg = CONFIG || {};
  // First-party log to our own backend (works even with no paid trackers
  // configured — powers the in-CMS Reports dashboard).
  try {
    fetch(`${API_URL}/api/content/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: eventName,
        location: params.location || '',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        visitor_id: _getVisitorId(),
        session_id: _getSessionId(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* ignore */ }
  // Meta Pixel — standard event names map directly; custom events use 'trackCustom'.
  if (LOADED.meta && window.fbq) {
    const META_STANDARD = ['PageView', 'ViewContent', 'Lead', 'CompleteRegistration', 'Contact', 'Schedule', 'Search', 'AddToCart', 'Purchase'];
    if (META_STANDARD.includes(eventName)) window.fbq('track', eventName, params);
    else window.fbq('trackCustom', eventName, params);
  }
  // GA4
  if (LOADED.ga4 && window.gtag) {
    window.gtag('event', eventName, params);
  }
  // Google Ads conversion (only for mapped events)
  if (LOADED.gads && window.gtag && cfg.google_ads_id) {
    let label = null;
    if (eventName === 'Lead' && cfg.google_ads_lead_label) label = cfg.google_ads_lead_label;
    if (eventName === 'Schedule' && cfg.google_ads_reservation_label) label = cfg.google_ads_reservation_label;
    if (label) window.gtag('event', 'conversion', { send_to: `${cfg.google_ads_id}/${label}`, ...params });
  }
  // TikTok
  if (LOADED.tiktok && window.ttq) {
    window.ttq.track(eventName, params);
  }
  // PostHog
  if (LOADED.posthog && window.posthog) {
    window.posthog.capture(eventName, params);
  }
};

/** Fire PageView on every route change. Meta Pixel and GA4 fire automatically
 *  on init, but SPA route changes need explicit calls. */
export const trackPageView = (path) => {
  if (!isBrowser) return;
  // First-party log
  try {
    fetch(`${API_URL}/api/content/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'PageView',
        path,
        visitor_id: _getVisitorId(),
        session_id: _getSessionId(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* ignore */ }
  if (LOADED.meta && window.fbq) window.fbq('track', 'PageView');
  if (LOADED.ga4 && window.gtag) {
    const cfg = CONFIG || {};
    if (cfg.ga4_measurement_id) {
      window.gtag('config', cfg.ga4_measurement_id, { page_path: path });
    }
  }
  if (LOADED.tiktok && window.ttq) window.ttq.page();
  if (LOADED.posthog && window.posthog) window.posthog.capture('$pageview', { path });
};
